import { usersId, type UsersId } from "./Users";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for media_blob */
export type MediaBlobId = string & { __flavor: "MediaBlobId" };

/**
 * Represents the table public.media_blob
 * @uniqueConstraints {"media_blob_pkey":{"name":"media_blob_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"media_blob_hash_unique":{"name":"media_blob_hash_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"hash","definition":"hash","predicate":null}]},"idx_media_blob_hash":{"name":"idx_media_blob_hash","isPrimary":false,"isUnique":false,"columns":[{"name":"hash","definition":"hash","predicate":null}]},"idx_media_blob_created_at":{"name":"idx_media_blob_created_at","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]},"idx_media_blob_mime_type":{"name":"idx_media_blob_mime_type","isPrimary":false,"isUnique":false,"columns":[{"name":"mime_type","definition":"mime_type","predicate":null}]},"idx_media_blob_created_by":{"name":"idx_media_blob_created_by","isPrimary":false,"isUnique":false,"columns":[{"name":"created_by","definition":"created_by","predicate":null}]}}
 */
export default interface MediaBlobTable {
  id: ColumnType<MediaBlobId, MediaBlobId | undefined, MediaBlobId>;

  hash: ColumnType<string, string, string>;

  originalFilename: ColumnType<string, string, string>;

  mimeType: ColumnType<string, string, string>;

  fileSize: ColumnType<number, number, number>;

  width: ColumnType<number | null, number | null, number | null>;

  height: ColumnType<number | null, number | null, number | null>;

  storageKey: ColumnType<string, string, string>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  createdBy: ColumnType<UsersId, UsersId, UsersId>;
}

export type MediaBlob = Selectable<MediaBlobTable>;

export type NewMediaBlob = Insertable<MediaBlobTable>;

export type MediaBlobUpdate = Updateable<MediaBlobTable>;

export const mediaBlobId = z.string() as unknown as z.Schema<MediaBlobId>;

export const mediaBlob = z.object({
  id: mediaBlobId,
  hash: z.string(),
  originalFilename: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  storageKey: z.string(),
  createdAt: z.date(),
  createdBy: usersId,
});

export const mediaBlobInitializer = z.object({
  id: mediaBlobId.optional(),
  hash: z.string(),
  originalFilename: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  width: z.number().optional().nullable(),
  height: z.number().optional().nullable(),
  storageKey: z.string(),
  createdAt: z.date().optional(),
  createdBy: usersId,
});

export const mediaBlobMutator = z.object({
  id: mediaBlobId.optional(),
  hash: z.string().optional(),
  originalFilename: z.string().optional(),
  mimeType: z.string().optional(),
  fileSize: z.number().optional(),
  width: z.number().optional().nullable(),
  height: z.number().optional().nullable(),
  storageKey: z.string().optional(),
  createdAt: z.date().optional(),
  createdBy: usersId.optional(),
});

// ── constraints for MediaBlob ─────────────────────────────────
export const MediaBlobConstraints = {
  media_blob_pkey: {
    name: "media_blob_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  media_blob_hash_unique: {
    name: "media_blob_hash_unique",
    isPrimary: false,
    isUnique: true,
    columns: [{ name: "hash", definition: "hash", predicate: null }],
  },
  idx_media_blob_hash: {
    name: "idx_media_blob_hash",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "hash", definition: "hash", predicate: null }],
  },
  idx_media_blob_created_at: {
    name: "idx_media_blob_created_at",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
  idx_media_blob_mime_type: {
    name: "idx_media_blob_mime_type",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "mime_type", definition: "mime_type", predicate: null }],
  },
  idx_media_blob_created_by: {
    name: "idx_media_blob_created_by",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_by", definition: "created_by", predicate: null }],
  },
} as const;
export type MediaBlobConstraintsType = typeof MediaBlobConstraints;
